import { useEffect, useMemo, useState } from 'react';
import type { PaymentStats } from '../models/PaymentModels';
import { PaymentService } from '../services/PaymentService';

/**
 * 대시보드 월 결제 통계 연월
 * - 차트에서 일 선택으로 목록이 열린 경우: 해당 일이 속한 달(차트 연동)
 * - 그 외: 주간 캘린더 selectedDate 기준 달
 */
export function useSelectedMonthPaymentStats(
  selectedDate: Date,
  chartLinkedYearMonth: string | null = null
) {
  const yearMonth = useMemo(() => {
    if (chartLinkedYearMonth && /^\d{4}-\d{2}$/.test(chartLinkedYearMonth)) {
      return chartLinkedYearMonth;
    }
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }, [selectedDate, chartLinkedYearMonth]);

  const monthTitleLabel = useMemo(() => {
    const parts = yearMonth.split('-');
    if (parts.length >= 2) {
      return `${parts[1].padStart(2, '0')}월`;
    }
    return `${String(selectedDate.getMonth() + 1).padStart(2, '0')}월`;
  }, [yearMonth, selectedDate]);

  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStats(null);
    PaymentService.getPaymentStats(yearMonth)
      .then(s => {
        if (!cancelled) setStats(s);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setStats(null);
          setError(e instanceof Error ? e.message : '결제 통계를 불러오지 못했습니다.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [yearMonth]);

  return { stats, loading, error, yearMonth, monthTitleLabel };
}
